// ============================================================================
//  GdrgRecodeDialog — 21 decisions instead of 233 claims
// ----------------------------------------------------------------------------
//  WHAT IT IS FOR
//
//  The catalogue holds one condition under two codes: Malaria is OPDC06A for a
//  consultation and MEDI28A for an admission — one ICD-10, two billing codes, each
//  correct in its own setting. An upload put the OUT-PATIENT code on diagnoses
//  belonging to ADMISSIONS: 449 rows across 353 claims.
//
//  "Fix all" on the "Admission billed on an out-patient service" tile already
//  repairs the rows whose ICD-10 resolves to a single admissible code. For the rest
//  it says "Open the claim and pick one — this will not choose for you", and that
//  is 233 claims opened one at a time.
//
//  THE INSIGHT THIS SCREEN IS BUILT ON: the ambiguity is per DIAGNOSIS, not per
//  claim. Those rows are 21 questions. "What does Pneumonia become for an adult"
//  is asked once and answers 62 claims.
//
//  WHAT IT WILL NOT DO
//
//  It will not choose. Each row arrives with the likeliest candidate pre-selected —
//  the band suffix matching the patient, so an adult sees MEDI28A above MEDI28 —
//  but that is an ORDERING, not an answer, and the tariffs differ. Deliberately not
//  ordered by price: dearest-first is upcoding, cheapest-first gives the facility
//  away.
//
//  It never touches the ICD-10 or the diagnosis text. What the patient was
//  diagnosed with is not a billing decision. Only the G-DRG moves.
//
//  Two things are shown and left alone rather than hidden:
//
//    DEAD ENDS   rows whose ICD-10 has no admissible code in the catalogue at all.
//                B96.81 is not in it; A09 is there only under out-patient codes.
//                That is a catalogue gap and the fix is an import, not a re-code.
//    DETENTION   a ZOOM02 code on a stay of one night or less is what detention IS.
//                Correct, and excluded unless explicitly asked for.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, Wrench, AlertTriangle, Info } from 'lucide-react';
import {
  claimStoreDiagnosisGdrgPreview, claimStoreDiagnosisGdrgApply,
} from '../BaseURL';
import { confirmClaim, notifyClaim } from './claimsDialog';
import useDialogGuard from './dialogGuard';

const BRAND = {
  green: '#1A9B7F', greenDark: '#137a63', greenSoft: '#e8f5f1',
  line: '#e2e8f0', ink: '#0f172a', inkSoft: '#475569', inkFaint: '#94a3b8',
  head: '#f1f5f9', canvas: '#f8fafc', danger: '#b91c1c', dangerSoft: '#fef2f2',
  amber: '#92400e', amberSoft: '#fffbeb', amberLine: '#fde68a', navy: '#123A6D',
};
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';
const n0 = (v) => Number(v || 0).toLocaleString();

export default function GdrgRecodeDialog({ scope, onClose, onDone }) {
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  //  { "J18.9|adult": "MEDI30A" }. Seeded from the first candidate of each group —
  //  the ordering, not a decision, and every one is visible and changeable before
  //  anything is written.
  const [choices, setChoices] = useState({});
  const [includeDetention, setIncludeDetention] = useState(false);
  const [showDead, setShowDead] = useState(false);
  //  Every decision in `choices` is unwritten until Apply, and a plan can run to
  //  twenty-odd groups. The backdrop no longer discards them.
  const { panelRef, backdropProps } = useDialogGuard();

  const load = useCallback(() => {
    setError(null);
    setPlan(null);
    axios.post(claimStoreDiagnosisGdrgPreview, { ...scope, includeDetention })
      .then(({ data }) => {
        setPlan(data);
        const seed = {};
        for (const g of data.groups || []) {
          if (g.candidates && g.candidates.length) seed[g.key] = g.candidates[0].gdrg_code;
        }
        setChoices(seed);
      })
      .catch((e) => setError(e?.response?.data?.message || e.message));
  }, [scope, includeDetention]);

  useEffect(load, [load]);

  const groups = plan?.groups || [];
  const answered = useMemo(
    () => groups.filter((g) => choices[g.key]).length, [groups, choices]);
  const rowsAnswered = useMemo(
    () => groups.filter((g) => choices[g.key]).reduce((n, g) => n + g.rowCount, 0),
    [groups, choices]);
  const claimsAnswered = useMemo(
    () => groups.filter((g) => choices[g.key]).reduce((n, g) => n + g.claimCount, 0),
    [groups, choices]);

  const setOne = (key, code) => setChoices((c) => ({ ...c, [key]: code }));
  const clearOne = (key) => setChoices((c) => {
    const next = { ...c }; delete next[key]; return next;
  });

  const apply = async () => {
    if (!answered) return;
    const items = groups.filter((g) => choices[g.key]).slice(0, 10).map((g) => ({
      text: `${g.icd}${g.band ? ` (${g.band})` : ''} → ${choices[g.key]} `
        + `· ${n0(g.rowCount)} row(s) on ${n0(g.claimCount)} claim(s)`,
    }));
    if (answered > 10) items.push({ text: `…and ${answered - 10} more diagnoses` });
    //  The one thing a price-ordered list would have hidden. Said out loud because
    //  each candidate carries a different tariff.
    items.push({
      text: 'The ICD-10 and the diagnosis text are not changed — only the G-DRG.',
    });

    const ok = await confirmClaim({
      title: `Re-code ${n0(rowsAnswered)} diagnosis row(s)?`,
      lead: `${answered} diagnos${answered === 1 ? 'is' : 'es'} across `
        + `${n0(claimsAnswered)} claim(s). Each row's billing code changes to the code `
        + 'you picked for its diagnosis.',
      items,
      note: 'This cannot be undone from here. Every change is recorded in the '
        + 'activity log.',
      confirmText: 'Re-code',
      tone: 'danger',
    });
    if (!ok) return;

    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(claimStoreDiagnosisGdrgApply, {
        ...scope, includeDetention, choices, fingerprint: plan.fingerprint,
      });
      await notifyClaim({
        title: 'Done',
        lead: data.message,
        items: (data.refused || []).map((r) => ({ text: r, warn: true })),
        note: 'The counts on the screen behind this describe the month as it was — '
          + 'search again to see it as it now stands.',
      });
      if (onDone) onDone(data);
      load();
    } catch (e) {
      const res = e?.response;
      if (res?.status === 409) {
        setError(res.data?.message || 'The claims changed — check again.');
        load();
      } else {
        setError(res?.data?.message || e.message);
      }
    } finally { setBusy(false); }
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Set the G-DRG on admission diagnoses"
      {...backdropProps}
      style={{
        position: 'fixed', inset: 0, zIndex: 1500,
        background: 'rgba(15,23,42,.5)',
        //  overflow:auto with margin:auto on the child, never align-items:center —
        //  a flex container cannot scroll to content centring has pushed above its
        //  own top edge, which is how a tall dialog loses its header.
        display: 'flex', overflow: 'auto', padding: '24px 16px',
      }}>
      <div ref={panelRef}
        style={{
          margin: 'auto', width: '100%', maxWidth: 860, background: '#fff',
          borderRadius: 14, overflow: 'hidden', border: `1px solid ${BRAND.line}`,
          boxShadow: '0 24px 60px rgba(15,23,42,.32)',
        }}>

        <div style={{
          display: 'flex', gap: 12, alignItems: 'flex-start',
          padding: '14px 16px', background: BRAND.navy, color: '#fff',
        }}>
          <Wrench size={18} style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 9.5, fontWeight: 800, letterSpacing: '.13em',
              textTransform: 'uppercase', color: 'rgba(255,255,255,.55)',
            }}>Admissions billed on an out-patient G-DRG</div>
            <div style={{ fontSize: 17, fontWeight: 800 }}>
              {plan
                ? `${n0(plan.counts.groups)} decision${plan.counts.groups === 1 ? '' : 's'}`
                : 'Reading the claims…'}
            </div>
            {plan && (
              <div style={{
                fontSize: 12.5, color: 'rgba(255,255,255,.8)', marginTop: 3,
                lineHeight: 1.5,
              }}>
                They settle <strong>{n0(plan.counts.rows)}</strong> diagnosis row(s) on{' '}
                <strong>{n0(plan.counts.claims)}</strong> claim(s). One answer per
                diagnosis, applied to every claim carrying it.
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{
            flexShrink: 0, width: 30, height: 30, borderRadius: 9, cursor: 'pointer',
            border: '1px solid rgba(255,255,255,.22)', color: '#fff',
            background: 'rgba(255,255,255,.14)', fontSize: 13, lineHeight: 1,
          }}>✕</button>
        </div>

        <div style={{ maxHeight: 'calc(100vh - 230px)', overflowY: 'auto', padding: 14 }}>
          {error && (
            <div style={{
              marginBottom: 12, padding: '9px 11px', borderRadius: 8, fontSize: 12,
              background: BRAND.dangerSoft, color: BRAND.danger,
              border: `1px solid ${BRAND.danger}33`,
            }}>{error}</div>
          )}

          {!plan && !error && (
            <div style={{ padding: 30, textAlign: 'center', color: BRAND.inkFaint,
              fontSize: 12.5 }}>
              <Loader2 size={16} style={{ verticalAlign: -3, marginRight: 6 }} />
              Grouping the diagnoses…
            </div>
          )}

          {plan && groups.length === 0 && (
            <div style={{
              padding: '12px 14px', borderRadius: 9, fontSize: 12.5, lineHeight: 1.6,
              background: BRAND.greenSoft, border: `1px solid ${BRAND.green}44`,
              color: BRAND.ink,
            }}>
              <strong>Nothing to decide.</strong> No admission diagnosis in this month
              is on an out-patient G-DRG that the catalogue can offer a replacement
              for.
            </div>
          )}

          {plan && groups.map((g) => (
            <div key={g.key} style={{
              border: `1px solid ${choices[g.key] ? BRAND.green : BRAND.amberLine}`,
              background: choices[g.key] ? BRAND.greenSoft : BRAND.amberSoft,
              borderRadius: 10, padding: 11, marginBottom: 9,
            }}>
              <div style={{
                display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap',
                marginBottom: 7,
              }}>
                <span style={{ fontFamily: MONO, fontWeight: 800, fontSize: 13,
                  color: BRAND.ink }}>{g.icd}</span>
                {g.band && (
                  <span style={{
                    fontSize: 9.5, fontWeight: 800, letterSpacing: '.05em',
                    textTransform: 'uppercase', padding: '1px 6px', borderRadius: 4,
                    background: '#fff', border: `1px solid ${BRAND.line}`,
                    color: BRAND.inkSoft,
                  }}>{g.band}</span>
                )}
                <span style={{ fontSize: 12.5, color: BRAND.ink, flex: 1, minWidth: 0 }}>
                  {g.name}
                </span>
                <span style={{ fontSize: 11.5, fontWeight: 800, color: BRAND.inkSoft }}>
                  {n0(g.rowCount)} row(s) · {n0(g.claimCount)} claim(s)
                </span>
              </div>

              <div style={{ fontSize: 11, color: BRAND.inkSoft, marginBottom: 7 }}>
                now billed on{' '}
                {g.was.map((w) => `${w.code} (${n0(w.rows)})`).join(', ')}
                {g.names.length > 1 && (
                  <> · written {g.names.length} different ways on these claims</>
                )}
              </div>

              <div style={{ display: 'flex', gap: 7, alignItems: 'center',
                flexWrap: 'wrap' }}>
                <span style={{
                  fontSize: 9.5, fontWeight: 800, letterSpacing: '.05em',
                  textTransform: 'uppercase', color: BRAND.inkFaint,
                }}>Becomes</span>
                {g.candidates.map((c) => {
                  const on = choices[g.key] === c.gdrg_code;
                  return (
                    <button key={c.gdrg_code} type="button"
                      onClick={() => (on ? clearOne(g.key) : setOne(g.key, c.gdrg_code))}
                      title={c.tariffDisagrees
                        ? `${c.name} — the catalogue lists this code more than once at `
                          + 'different tariffs'
                        : c.name}
                      style={{
                        padding: '4px 10px', borderRadius: 999, cursor: 'pointer',
                        fontFamily: MONO, fontSize: 11.5, fontWeight: on ? 800 : 600,
                        border: `1px solid ${on ? BRAND.greenDark : BRAND.line}`,
                        background: on ? BRAND.greenDark : '#fff',
                        color: on ? '#fff' : BRAND.inkSoft,
                      }}>
                      {c.gdrg_code}
                      {c.tariffDisagrees && <span aria-hidden="true"> ⚠</span>}
                    </button>
                  );
                })}
                {g.obvious && (
                  <span style={{ fontSize: 10.5, color: BRAND.greenDark,
                    fontWeight: 700 }}>only one option</span>
                )}
                {!choices[g.key] && (
                  <span style={{ fontSize: 10.5, color: BRAND.amber, fontWeight: 700 }}>
                    left as it is
                  </span>
                )}
              </div>
            </div>
          ))}

          {/*  Not decisions. Shown so the numbers reconcile and so the catalogue
               gap is visible to whoever can close it. */}
          {plan && (plan.deadEnds?.length > 0 || plan.detention?.rows > 0) && (
            <div style={{
              marginTop: 12, border: `1px solid ${BRAND.line}`, borderRadius: 9,
              background: BRAND.canvas, padding: 11, fontSize: 12, lineHeight: 1.6,
              color: BRAND.inkSoft,
            }}>
              {plan.detention?.rows > 0 && (
                <div style={{ marginBottom: plan.deadEnds?.length ? 8 : 0 }}>
                  <Info size={12} style={{ verticalAlign: -2, marginRight: 5 }} />
                  <strong>{n0(plan.detention.rows)} detention row(s)</strong> on{' '}
                  {n0(plan.detention.claims)} claim(s) are left alone — a ZOOM02 code
                  on a stay of one night or less is correct.
                  {plan.detention.overOneNight > 0 && (
                    <> {n0(plan.detention.overOneNight)} of them sit on a longer stay,
                      which is not detention.</>
                  )}
                  {' '}
                  <label style={{ cursor: 'pointer', fontWeight: 700,
                    color: BRAND.navy }}>
                    <input type="checkbox" checked={includeDetention}
                      onChange={(e) => setIncludeDetention(e.target.checked)}
                      style={{ marginRight: 4 }} />
                    include them
                  </label>
                </div>
              )}
              {plan.deadEnds?.length > 0 && (
                <div>
                  <AlertTriangle size={12} style={{ verticalAlign: -2, marginRight: 5,
                    color: BRAND.amber }} />
                  <strong>{n0(plan.counts.deadEnd)} row(s)</strong> across{' '}
                  {plan.deadEnds.length} diagnos{plan.deadEnds.length === 1 ? 'is' : 'es'}{' '}
                  cannot be re-coded from here: the catalogue has no admissible G-DRG
                  for their ICD-10. That is an import to fix, not a code to pick.{' '}
                  <button type="button" onClick={() => setShowDead((v) => !v)}
                    style={{
                      border: 0, background: 'none', padding: 0, font: 'inherit',
                      fontSize: 12, fontWeight: 700, cursor: 'pointer',
                      color: BRAND.navy, textDecoration: 'underline',
                    }}>{showDead ? 'hide' : 'show which'}</button>
                  {showDead && (
                    <div style={{ marginTop: 6, maxHeight: 150, overflowY: 'auto' }}>
                      {plan.deadEnds.map((e) => (
                        <div key={e.key} style={{
                          display: 'flex', gap: 8, padding: '3px 0', fontSize: 11.5,
                        }}>
                          <span style={{ fontFamily: MONO, fontWeight: 700,
                            minWidth: 74 }}>{e.icd}</span>
                          <span style={{ minWidth: 46, color: BRAND.inkFaint }}>
                            {e.band || 'any'}
                          </span>
                          <span style={{ minWidth: 58, textAlign: 'right',
                            fontWeight: 700 }}>{n0(e.rowCount)}</span>
                          <span style={{ flex: 1, minWidth: 0, color: BRAND.inkFaint }}>
                            {e.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{
          display: 'flex', gap: 8, alignItems: 'center', padding: '11px 14px',
          borderTop: `1px solid ${BRAND.line}`, background: BRAND.canvas,
        }}>
          <span style={{ flex: 1, fontSize: 11.5, color: BRAND.inkSoft }}>
            {plan
              ? <>{answered} of {groups.length} answered ·{' '}
                  <strong>{n0(rowsAnswered)}</strong> row(s) would change. Anything
                  left un-answered stays exactly as it is.</>
              : ' '}
          </span>
          <button type="button" onClick={onClose} style={{
            padding: '8px 15px', borderRadius: 8, fontSize: 12.5, fontWeight: 700,
            border: `1px solid ${BRAND.line}`, background: '#fff',
            color: BRAND.inkSoft, cursor: 'pointer', font: 'inherit',
          }}>Close</button>
          <button type="button" onClick={apply} disabled={!answered || busy}
            style={{
              padding: '8px 16px', borderRadius: 8, fontSize: 12.5, fontWeight: 800,
              border: 0, cursor: !answered || busy ? 'default' : 'pointer',
              background: !answered || busy ? BRAND.inkFaint : BRAND.green,
              color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
            {busy ? <Loader2 size={14} /> : <Wrench size={14} />}
            {busy ? 'Writing…' : `Re-code ${n0(rowsAnswered)} row(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}
